import React from 'react';
import styled, { DefaultTheme } from 'styled-components/macro';
import {
  TYPOGRAPHY_WEIGHT,
  TYPOGRAPHY_TRANSFORM,
  TYPOGRAPHY_ALIGN,
  TYPOGRAPHY_FAMILY,
} from '../types';
import {
  sizesMap,
  fontFamilyMap,
  weightMap,
  textLineHeight,
  textLetterSpacing,
  typographyMarginBottom,
} from '../utils';
import { SIZE } from '../../types';

type TEXT_TYPE = 'label';
type MARGIN = 'none';
type DISPLAY = 'block' | 'inline-block' | 'inline-flex';
type OPACITY = 0 | 25 | 50 | 75 | 100;

type TextStyledProps = {
  size: SIZE;
  weight: TYPOGRAPHY_WEIGHT;
  textTransform: TYPOGRAPHY_TRANSFORM;
  textOpacity: number;
  textDisplay: string;
  align: TYPOGRAPHY_ALIGN;
  familyType: TYPOGRAPHY_FAMILY;
  themeColor: (theme: DefaultTheme) => string;
  htmlFor?: string;
  margin?: MARGIN;
};

const TextStyled = styled.span<TextStyledProps>`
  display: ${(p) => p.textDisplay};
  font-family: ${(p) => p.theme.fonts[fontFamilyMap[p.familyType]]};
  font-weight: ${(p) => weightMap[p.weight]};
  font-size: ${(p) => sizesMap[p.size].size};
  line-height: ${textLineHeight};
  letter-spacing: ${textLetterSpacing};
  text-transform: ${(p) => p.textTransform};
  text-align: ${(p) => p.align};
  color: ${(p) => p.themeColor(p.theme)};
  padding: 0;
  opacity: ${(p) => p.textOpacity / 100};
  margin: ${(p) => typographyMarginBottom(p.margin === 'none')};
  align-items: center;
`;

type TextPropsBase = {
  type?: TEXT_TYPE;
  size?: SIZE;
  weight?: TYPOGRAPHY_WEIGHT;
  transform?: TYPOGRAPHY_TRANSFORM;
  margin?: MARGIN;
  display?: DISPLAY;
  align?: TYPOGRAPHY_ALIGN;
  familyType?: TYPOGRAPHY_FAMILY;
  opacity?: OPACITY;
  htmlFor?: string;
  id?: string;
  testid?: string;
  style?: React.CSSProperties;
  className?: string;
};

type TextProps = TextPropsBase &
  ({ children: React.ReactNode } | { innerHTML: string });

const Text = React.forwardRef<HTMLSpanElement | HTMLLabelElement, TextProps>(
  (props, ref) => {
    let {
      type,
      size = 'base',
      weight = 'normal',
      transform = 'none',
      align = 'left',
      familyType = 'sans',
      opacity = 100,
      margin,
      display = 'inline-flex',
      htmlFor,
      id,
      testid,
      style,
      className,
    } = props;

    const children = 'children' in props ? props.children : undefined;
    const innerHTML = 'innerHTML' in props ? props.innerHTML : undefined;

    let tag: 'span' | 'label' = 'span';

    let color = (theme: DefaultTheme) => theme.colors.secondary;
    if (type === 'label') {
      color = (theme) => theme.colors.label;
      familyType = props.familyType ?? 'serif';
    }

    tag = type === 'label' && htmlFor ? 'label' : 'span';

    const isOutput = id && !htmlFor;

    const newChildren =
      type === 'label' ? (
        children
      ) : isOutput ? (
        <output style={style} id={id}>
          {children}
        </output>
      ) : (
        children
      );

    return (
      <TextStyled
        ref={ref}
        as={tag}
        data-testid={testid}
        size={size}
        weight={weight}
        textDisplay={display}
        textTransform={transform}
        textOpacity={opacity}
        align={align}
        familyType={familyType}
        margin={margin}
        style={isOutput ? undefined : style}
        className={className}
        htmlFor={htmlFor}
        themeColor={color}
        dangerouslySetInnerHTML={innerHTML ? { __html: innerHTML } : undefined}
      >
        {newChildren}
      </TextStyled>
    );
  }
);

export default React.memo(Text);
